import { CommandError } from '../errors.js';
import { required } from './types.js';
function scenarioOr404(slice, scenarioId) {
    const found = required(slice, 'scenarios')[scenarioId];
    if (!found)
        throw new CommandError('not_found', `No such scenario: ${scenarioId}`);
    return found;
}
export const createScenario = {
    needs: ['scenarios'],
    apply(slice, command) {
        if (required(slice, 'scenarios')[command.scenarioId]) {
            throw new CommandError('conflict', `A scenario with id ${command.scenarioId} already exists`);
        }
        return {
            writes: [
                { t: 'scenarioCreate', scenarioId: command.scenarioId, name: command.name, copyFrom: null },
            ],
            inverse: [{ type: 'deleteScenario', scenarioId: command.scenarioId }],
            summary: `Created scenario "${command.name}" (empty)`,
            touched: [`scenario:${command.scenarioId}`],
        };
    },
};
export const duplicateScenario = {
    needs: ['scenarios'],
    apply(slice, command) {
        const source = scenarioOr404(slice, command.sourceScenarioId);
        if (required(slice, 'scenarios')[command.scenarioId]) {
            throw new CommandError('conflict', `A scenario with id ${command.scenarioId} already exists`);
        }
        return {
            // The copy is a server-side bulk insert rather than a read-then-write, so duplicating a large
            // design does not cross the wire twice or race with a concurrent edit to the source.
            writes: [
                {
                    t: 'scenarioCreate',
                    scenarioId: command.scenarioId,
                    name: command.name,
                    copyFrom: command.sourceScenarioId,
                },
            ],
            inverse: [{ type: 'deleteScenario', scenarioId: command.scenarioId }],
            summary: `Created scenario "${command.name}" (from "${source.name}")`,
            touched: [`scenario:${command.scenarioId}`],
        };
    },
};
export const renameScenario = {
    needs: ['scenarios'],
    apply(slice, command) {
        const existing = scenarioOr404(slice, command.scenarioId);
        return {
            writes: [{ t: 'scenarioPatch', scenarioId: command.scenarioId, patch: { name: command.name } }],
            inverse: [
                { type: 'renameScenario', scenarioId: command.scenarioId, name: existing.name },
            ],
            summary: `Renamed scenario "${existing.name}" → "${command.name}"`,
            touched: [`scenario:${command.scenarioId}`],
        };
    },
};
export const deleteScenario = {
    needs: ['scenarios'],
    apply(slice, command) {
        const scenarios = required(slice, 'scenarios');
        const existing = scenarioOr404(slice, command.scenarioId);
        if (Object.keys(scenarios).length <= 1) {
            throw new CommandError('conflict', 'At least one scenario is required');
        }
        return {
            writes: [{ t: 'scenarioDelete', scenarioId: command.scenarioId }],
            // A whole design goes with it — buckets, heads, overrides, TSAs, build-ups. Reconstructing that
            // from an event would mean carrying the entire scenario in the log, so this is honestly
            // irreversible rather than offering an undo that restores an empty shell.
            inverse: null,
            summary: `Deleted scenario "${existing.name}" and its design`,
            touched: [`scenario:${command.scenarioId}`],
        };
    },
};
export const setDefaultScenario = {
    needs: ['scenarios'],
    apply(slice, command) {
        const scenarios = required(slice, 'scenarios');
        const existing = scenarioOr404(slice, command.scenarioId);
        const previous = Object.entries(scenarios).find(([, s]) => s.isDefault)?.[0];
        return {
            writes: [{ t: 'scenarioDefault', scenarioId: command.scenarioId }],
            inverse: previous ? [{ type: 'setDefaultScenario', scenarioId: previous }] : null,
            // Project-level, not per-user: this is what loads when somebody opens the project, as opposed to
            // which scenario you personally happen to be working in.
            summary: `Scenario "${existing.name}" is now the one the project opens on`,
            touched: ['scenario:default'],
        };
    },
};
//# sourceMappingURL=scenarios.js.map